import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';

const EmployeeHistoryReportPage = () => {
  const [loading, setLoading] = useState(true);
  const [handovers, setHandovers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [viewMode, setViewMode] = useState('current'); // 'current' | 'overall'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch handovers for overall view
        const handoverRes = await api.getHandovers({ limit: 1000 });
        if (handoverRes.success) setHandovers(handoverRes.data || []);

        // Fetch all assets for current view (we filter client-side)
        const assetRes = await api.getAssets({ limit: 2000 });
        if (assetRes.success) {
          // Keep only assets that are currently assigned to someone
          const assigned = (assetRes.data || []).filter(
            (a) => a.assignedTo && a.status !== 'available'
          );
          setAssets(assigned);
        }
      } catch (error) {
        console.error('Error loading employee history report data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- CURRENT VIEW: group by assignedTo from actual asset records ---
  const currentRows = useMemo(() => {
    const map = new Map();
    assets.forEach((asset) => {
      const emp = asset.assignedTo;
      if (!emp) return;
      const empId = emp._id || emp.id || 'unknown';
      const name = emp.name || 'Unknown Employee';

      if (!map.has(empId)) {
        map.set(empId, {
          employeeId: empId,
          name,
          total: 0,
          pending: 0,
          acknowledged: 0,
          issues: 0,
          lastAssigned: null,
        });
      }
      const entry = map.get(empId);
      entry.total += 1;

      // pending = checked out, not yet acknowledged by employee
      if (asset.status === 'pending') {
        entry.pending += 1;
      }
      // issue_reported = still with user but reported a problem
      else if (asset.status === 'issue_reported') {
        entry.issues += 1;
      }
      // received / in_transfer = employee confirmed receipt
      else if (asset.status === 'received' || asset.status === 'in_transfer') {
        entry.acknowledged += 1;
      }

      // Track the most recent assignment date
      const date = asset.assignedDate ? new Date(asset.assignedDate) : null;
      if (date && (!entry.lastAssigned || date > entry.lastAssigned)) {
        entry.lastAssigned = date;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [assets]);

  // --- OVERALL VIEW: group by assignedTo from handover records ---
  const overallRows = useMemo(() => {
    const map = new Map();
    handovers.forEach((h) => {
      const empId = h.assignedTo?._id || h.assignedTo?.id || 'unknown';
      const name = h.assignedTo?.name || 'Unknown Employee';
      if (!map.has(empId)) {
        map.set(empId, {
          employeeId: empId,
          name,
          total: 0,
          pending: 0,
          received: 0,
          issues: 0,
          lastAssigned: null,
        });
      }
      const entry = map.get(empId);
      entry.total += 1;
      if (h.acknowledgementStatus === 'pending') entry.pending += 1;
      else if (h.acknowledgementStatus === 'acknowledged') entry.received += 1;
      else if (h.acknowledgementStatus === 'issue_reported' || h.issues) entry.issues += 1;
      const date = new Date(h.assignedDate || h.createdAt);
      if (!entry.lastAssigned || date > entry.lastAssigned) entry.lastAssigned = date;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [handovers]);

  const rows = viewMode === 'current' ? currentRows : overallRows;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const Badge = ({ count, bg, color }) =>
    count > 0 ? (
      <span style={{ background: bg, color, borderRadius: '4px', padding: '2px 8px', fontWeight: 600, fontSize: '12px' }}>
        {count}
      </span>
    ) : (
      <span style={{ color: '#a0aec0' }}>0</span>
    );

  if (loading) return <Loader />;

  return (
    <div className="employee-history-report-page" style={{ padding: '16px' }}>
      <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ fontSize: '14px', color: '#718096' }}>
            {viewMode === 'current'
              ? 'Employees who currently have assets assigned to them.'
              : 'All-time assignment and acknowledgement history per employee.'}
          </div>

          {/* Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: '8px', padding: '3px', gap: '2px' }}>
              {[
                { key: 'current', label: '⚡ Current' },
                { key: 'overall', label: '📊 Overall' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key)}
                  style={{
                    padding: '6px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 600, transition: 'all 0.2s ease',
                    background: viewMode === key ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: viewMode === key ? 'white' : '#718096',
                    boxShadow: viewMode === key ? '0 2px 6px rgba(102,126,234,0.4)' : 'none',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '12px', color: '#a0aec0' }}>
              {rows.length} employee{rows.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div style={{
          background: viewMode === 'current' ? '#ebf8ff' : '#faf5ff',
          border: `1px solid ${viewMode === 'current' ? '#bee3f8' : '#e9d8fd'}`,
          borderRadius: '6px', padding: '8px 12px', marginBottom: '14px',
          fontSize: '12px', color: viewMode === 'current' ? '#2b6cb0' : '#553c9a',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          {viewMode === 'current' ? (
            <><span>⚡</span><span><strong>Current View:</strong> Based on assets currently checked out. Issues = asset still with employee but problem reported. Acknowledged = employee confirmed receipt.</span></>
          ) : (
            <><span>📊</span><span><strong>Overall View:</strong> Complete all-time handover records for all employees including returned assets.</span></>
          )}
        </div>

        {/* Table */}
        {rows.length === 0 ? (
          <p style={{ fontSize: '13px', color: '#a0aec0', margin: 0 }}>
            {viewMode === 'current' ? 'No employees currently have assets assigned.' : 'No handover history available.'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '700px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f7fafc' }}>
                  <th style={{ textAlign: 'left', padding: '10px 8px', color: '#4a5568', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Employee
                  </th>
                  <th style={{ textAlign: 'right', padding: '10px 8px', color: '#4a5568', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {viewMode === 'current' ? 'Total Current' : 'Total Handovers'}
                  </th>
                  <th style={{ textAlign: 'right', padding: '10px 8px', color: '#4a5568', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pending
                  </th>
                  <th style={{ textAlign: 'right', padding: '10px 8px', color: '#4a5568', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Acknowledged
                  </th>
                  <th style={{ textAlign: 'right', padding: '10px 8px', color: '#4a5568', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Issues
                  </th>
                  <th style={{ textAlign: 'right', padding: '10px 8px', color: '#4a5568', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Last Assigned
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={row.employeeId}
                    style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? 'white' : '#fdfdfe' }}
                  >
                    <td style={{ padding: '10px 8px', color: '#2d3748', fontWeight: 500 }}>{row.name}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: '#2d3748', fontWeight: 700 }}>{row.total}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                      <Badge count={row.pending} bg="#fef3cd" color="#9a6810" />
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                      <Badge count={viewMode === 'current' ? row.acknowledged : row.received} bg="#c6f6d5" color="#276749" />
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                      <Badge count={row.issues} bg="#fed7d7" color="#9b2c2c" />
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: '#718096', fontSize: '12px' }}>
                      {formatDate(row.lastAssigned)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeHistoryReportPage;
